import React, { useState } from 'react';
import { ShieldCheck, ShieldAlert, Loader2 } from 'lucide-react';
import { tabletopExercisesAPI, TabletopExercise, TabletopCoverageResult } from '../../services/tabletopService';

interface Props {
  projectId: string;
  exercise: TabletopExercise;
  coverage: TabletopCoverageResult | null;
  onApproved: (exercise: TabletopExercise) => void;
}

// The hard gate (plan 4.1, 4.4): approval is rejected, with the failing
// requirement named, if either mandatory readiness inject is absent. This
// component only ever reflects what the backend's coverage self-check
// already computed — it does not itself decide pass/fail.
const CoverageGatePage: React.FC<Props> = ({ projectId, exercise, coverage, onApproved }) => {
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApprove = async () => {
    setApproving(true);
    setError(null);
    try {
      const r = await tabletopExercisesAPI.approve(projectId, exercise.exerciseId);
      onApproved(r.data.exercise);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setApproving(false);
    }
  };

  if (exercise.status === 'approved' || exercise.status === 'running' || exercise.status === 'complete') {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-3">
        <ShieldCheck size={20} className="text-emerald-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-emerald-800">Approved</p>
          <p className="text-xs text-emerald-700 mt-0.5">
            Signed off by {exercise.approvedBy} at {exercise.approvedAt ? new Date(exercise.approvedAt).toLocaleString() : ''}.
          </p>
        </div>
      </div>
    );
  }

  if (!coverage) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
        Coverage self-check has not run yet.
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border p-5 flex flex-col gap-4 ${coverage.passed ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
      <div className="flex items-start gap-3">
        {coverage.passed
          ? <ShieldCheck size={20} className="text-emerald-600 flex-shrink-0 mt-0.5" />
          : <ShieldAlert size={20} className="text-red-500 flex-shrink-0 mt-0.5" />}
        <div>
          <p className={`text-sm font-semibold ${coverage.passed ? 'text-emerald-800' : 'text-red-800'}`}>
            {coverage.passed ? 'Coverage self-check passed' : 'Coverage self-check failed'}
          </p>
          <p className={`text-xs mt-0.5 ${coverage.passed ? 'text-emerald-700' : 'text-red-700'}`}>
            {coverage.passed
              ? 'Both mandatory readiness injects are present. This exercise can be approved.'
              : 'This exercise cannot be approved until every requirement below is present.'}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        {coverage.checkedItems.map(item => (
          <div key={item.label} className="flex items-center gap-2 text-sm">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${item.present ? 'bg-emerald-500' : 'bg-red-400'}`} />
            <span className={item.present ? 'text-navy' : 'text-red-700 font-medium'}>{item.label}</span>
          </div>
        ))}
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <button
        onClick={handleApprove}
        disabled={!coverage.passed || approving}
        className="self-start flex items-center gap-2 rounded-xl h-10 px-5 bg-primary text-white text-sm font-bold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-opacity"
      >
        {approving ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
        Approve exercise
      </button>
    </div>
  );
};

export default CoverageGatePage;
